import React, { useEffect } from 'react';
import { PromptContainer } from '../../v2BaseComponents/Prompt';
import { TextInput, View } from 'react-native';
import SearchInput from './SearchInput';
import IconButton from '../../v2BaseComponents/IconButton';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Divider from '../../v2BaseComponents/Divider';
import MobileChatsButton from './MobileChatsButton';
import DesktopChatHistory from './DesktopChatHistory';
import { useMedicalProfileSearch } from '../../../hooks/api/useMedicalProfileSearch';
import SearchResults from './SearchResults';
import useSearchText from './useSearchText';
import useSearchHistory from './useSearchHistory';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';

export type SearchPromptContentsOwnProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    medicalProfileId: string;
};

export type SearchPromptContentsProps = SearchPromptContentsOwnProps;

const SearchPromptContents: React.FC<SearchPromptContentsProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const {
        debouncedSearchText,
        nonDebouncedOnChange,
        nonDebouncedSearchText,
        onCloseAndClearSearchText,
        onCloseAndKeepSearchText,
        isLoadingSearchText,
    } = useSearchText({
        onClose: () => props.setIsOpen(false),
        medicalProfileId: props.medicalProfileId,
    });
    const profileTabNavigation = useProfilesTabNavigation();
    const { isLoadingSearchResults, searchResults } = useMedicalProfileSearch({
        medicalProfileId: props.medicalProfileId,
        text: debouncedSearchText,
    });
    const searchInputRef = React.useRef<TextInput>(null);
    const { historicalSearches, onPersistCurrentSearch } = useSearchHistory({
        medicalProfileId: props.medicalProfileId,
        searchText: debouncedSearchText,
    });

    useEffect(() => {
        if (props.isOpen) {
            const timeout = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);

            return () => clearTimeout(timeout);
        }
    }, [props.isOpen]);

    if (!props.isOpen || isLoadingSearchText) return null;

    return (
        <PromptContainer
            isOpen={props.isOpen}
            setIsOpen={(isOpen) => {
                if (!isOpen) {
                    onCloseAndClearSearchText();
                } else {
                    props.setIsOpen(true);
                }
            }}
            mobileMaxHeight={'90%'}
            mobileHeight={'95%'}
            desktopWidth={'80%'}
            desktopHeight={'80%'}
            renderPromptContents={(onClose) => (
                <View
                    style={{
                        alignSelf: 'stretch',
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    {!isMobile ? (
                        <>
                            <View
                                style={{
                                    alignSelf: 'stretch',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: mainContentPadding,
                                }}
                            >
                                <SearchInput
                                    style={{ flex: 1 }}
                                    placeholder="Search"
                                    value={nonDebouncedSearchText}
                                    onChange={nonDebouncedOnChange}
                                    ref={searchInputRef}
                                />
                                <IconButton icon="X" onPress={onCloseAndClearSearchText} variant="transparent" />
                            </View>
                            <Divider horizontal />
                        </>
                    ) : null}
                    <View style={{ flex: 1, flexDirection: 'row' }}>
                        <View style={{ flex: 1, flexDirection: 'column' }}>
                            <SearchResults
                                isLoadingSearch={isLoadingSearchResults}
                                searchResults={searchResults}
                                searchText={debouncedSearchText}
                                onSelectedResult={() => {
                                    onPersistCurrentSearch();
                                    onCloseAndKeepSearchText();
                                }}
                                medicalProfileId={props.medicalProfileId}
                                historicalSearches={historicalSearches}
                                onSetSearchText={nonDebouncedOnChange}
                                onPromptChat={(prompt) => {
                                    profileTabNavigation.navigate('chat', {
                                        profileId: props.medicalProfileId,
                                        defaultPrompt: prompt,
                                        disableSetDefaultChat: 'true',
                                    });
                                    onClose();
                                }}
                            />
                        </View>
                        {!isMobile ? (
                            <DesktopChatHistory medicalProfileId={props.medicalProfileId} onClose={onClose} />
                        ) : null}
                    </View>
                    {isMobile ? (
                        <>
                            <Divider horizontal />
                            <View
                                style={{
                                    alignSelf: 'stretch',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: mainContentPadding,
                                }}
                            >
                                <SearchInput
                                    style={{ flex: 1 }}
                                    placeholder="Search"
                                    value={nonDebouncedSearchText}
                                    onChange={nonDebouncedOnChange}
                                    ref={searchInputRef}
                                />
                                <MobileChatsButton
                                    onPress={() => {
                                        profileTabNavigation.navigate('chat', {
                                            profileId: props.medicalProfileId,
                                            defaultPrompt: nonDebouncedSearchText,
                                            disableSetDefaultChat: 'true',
                                        });
                                        onClose();
                                    }}
                                />
                            </View>
                        </>
                    ) : null}
                </View>
            )}
        />
    );
};

export default React.memo(SearchPromptContents);
